import React, {useEffect, useState} from "react";
import {createNewDeal, dealData, dealLoad, saveDealParams} from "./deal";
import {loadUserOptions, userOptions} from "../../user";
import {exceptionsTitle} from "../../dictionaries";
import {RegistrationTypes, RolesEnums} from "../../enums";
import {DKPAddParamsContainer, DKPDealContainer} from "./containers/DealDKPContainer";
import {DealShareRegistrationContainer, ShareAddParamsContainer} from "./containers/DealShareRegistrationContainer";
import {DealPrevRight1998} from "./containers/DealPrevRight1998";
import {DealRegMortgageAgreement} from "./containers/DealRegMortgageAgreement";
import {DealAddStatementContainer, DKPAddStatementParamsContainer} from "./containers/DealAddStatementContainer";
import {RepaymentMortgageParamsContainer} from "./containers/DealRepaymentMortgageContainer";
import {AssignmentAgreementParamsContainer, DealAssignmentAgreement} from "./containers/DealAssignmentAgreement";
import {DealMortgageRegistrationContainer, DealMortgageRegistrationParamsContainer} from "./containers/DealMortgageRegistrationContainer";
import {DealParamsMortgageAndExpress} from "./components/DealParams";
import {DealMakingChangesContainer, MakingChangesParamsContainer} from "./containers/DealMakingChanges";
import {DealEnteringInfoToEGRN, EnteringInfoParamsContainer} from "./containers/DealEnteringInfoToEGRN";
import {DealRegRentAgreementContainer} from "./containers/DealRegRentAgreementContainer";
import {DealGKUwRightContainer} from "./containers/DealGKUwRight";
import {
  DealChangeSpecificationContainer,
  MakingChangesSpecificationContainer
} from "./containers/DealChangeSpecification";
import {DealGKUwoRightContainer} from "./containers/DealGKUwoRight";
import {AddRegShareCommonPropContainer, DealRegShareCommonPropContainer} from "./containers/DealRegShareCommonProp";
import {RegRightDealContainer} from "./containers/DealRegRightContainer";
import {DealActionsEnums, handleDealAction} from "../../actions";
import {waitInputComplete} from "../../fn";
import {useCustomeTheme} from "../../domains/common/components/ThemeContext";
import {useNotify} from "../../domains/common/components/AlertNotify";
import {PropertyDeregistrationDealContainer} from "./containers/DealPropertyDereg";
import {
  DealTerminationParamsContainer,
  TerminationOfOwnershipContainer,
} from './containers/DealTerminationOfOwnershipContainer';
const queryParameters = new URLSearchParams(window.location.search)
const id = queryParameters.get("id")

/**
 *
 * @return {{changeDealType: ((function(*): Promise<void>)|*), addParamsContainer: unknown, dealContainer: unknown, dataHasLoaded: boolean}}
 */
export const useDeal = () => {
  const [dealContainer, setDealContainer] = useState(null);
  const [addParamsContainer, setAddParamsContainer] = useState(null);
  const [dataHasLoaded, setDataHasLoaded] = useState(false);
  const [title, setTitle] = useState('');

  const {toggleTheme} = useCustomeTheme();
  const {openNotify} = useNotify();

  useEffect(() => {
    loadDeal();
    if (!id)
      setDataHasLoaded(true);
  }, []);

  const loadDeal = async () => {
    if (!id || dealData?.dealId)
      return;

    const result = await dealLoad(id);

    await loadUserOptions()

    toggleTheme(userOptions.theme)

    if (dealData.dealId) {
      setDataHasLoaded(true);
      setTitle(dealData.deal.title);
      handleDealContainer(dealData.type);
    } else {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "Сделка отсутствует",
        duration: 10000,
        type: 'error',
        positionH: 'right',
      })
    }
  }

  const changeDealType = async (e) => {
    if (dealData.dealId) {
      return;
    }

    const result = await createNewDeal(e.target.value);

    if (!result?.ok) {
      openNotify({
        msg: exceptionsTitle[result?.message] || result?.message || "При создании сделки возникла ошибка",
        duration: 3000,
        errors: result.errors,
        type: 'error',
        positionH: 'right',
      })

      return;
    }

    handleDealContainer(e.target.value)
  }

  const saveAddParams = async (value) => {
    const result = await saveDealParams(value);

    if (!value.title)
      handleDealAction(DealActionsEnums.UPDATE_DEAL_DATA);

    if (!result.ok) {
      openNotify({
        msg: exceptionsTitle[result.message] || result.message || "При изменении параметров возникла ошибка",
        duration: 5000,
        errors: result.errors,
        type: 'error',
        positionH: 'right',
      })
    }
  }

  const handleDealContainer = (type) => {
    const config = CONTAINER_CONFIG[type];

    if (!config) {
      openNotify({
        msg: `Неизвестный тип регистрации: ${type}`
      });
      return;
    }

    const commonProps = {
      dealId: dealData.dealId,
      dealData: dealData.deal,
      onChange: saveAddParams
    };

    setDealContainer(config.deal());
    setAddParamsContainer(config.params ? config.params(commonProps) : null);
  }

  const saveTitle = waitInputComplete((e) => {
    saveAddParams({title: e.target.value});

    setTitle(e.target.value)
  }, 1500)

  const makeTitle = () => {
    let side1 = '';
    let side2 = '';

    dealData.participants.map((participant) => {
      if (
        [RolesEnums.BUYER, RolesEnums.SHAREHOLDER, RolesEnums.PLEDGER, RolesEnums.RENTER].includes(participant.role) &&
        !side1 &&
        participant.lastName
      ) {
        side1 = `${participant.lastName} ${participant.firstName.slice(0, 1)}.${participant.middleName.slice(0, 1)}.`
      }

      if (
        [RolesEnums.SELLER, RolesEnums.LANDLORD, RolesEnums.DECLARANT, RolesEnums.PLEDGEE].includes(participant.role) &&
        !side2 &&
        participant.lastName
      ) {
        side2 = `${participant.lastName} ${participant.firstName.slice(0, 1)}.${participant.middleName.slice(0, 1)}.`
      }
    })

    if (!side1 && !side2) {
      openNotify({
        msg: "Участники не найдены",
        duration: 1500,
        type: 'error',
        positionH: 'right',
      })

      return;
    }

    const result = `${side1}${side1 ? ' - ' : ''}${side2} ${title}`;

    if (result.length > 128) {
      openNotify({
        msg: "Слишком длинное описание",
        duration: 1500,
        type: 'error',
        positionH: 'right',
      })

      return;
    }

    saveAddParams({title: result})

    setTitle(result);
  }

  return {
    dealContainer, dataHasLoaded, addParamsContainer, changeDealType, saveAddParams, title, saveTitle, makeTitle
  }
}

export const CONTAINER_CONFIG = {
  [RegistrationTypes.REGISTRATION_DKP]: {
    deal: () => <DKPDealContainer/>,
    params: (props) => <DKPAddParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.TERMINATION_DKP]: {
    deal: () => <DKPDealContainer/>,
    params: null
  },
  [RegistrationTypes.REGISTRATION_SHARE]: {
    deal: () => <DealShareRegistrationContainer/>,
    params: (props) => <ShareAddParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REG_PREV_RIGHT_1998]: {
    deal: () => <DealPrevRight1998/>,
    params: null
  },
  [RegistrationTypes.REG_MORTGAGE_AGREEMENT]: {
    deal: () => <DealRegMortgageAgreement/>,
    params: null
  },
  [RegistrationTypes.ADDITIONAL_STATEMENT]: {
    deal: () => <DealAddStatementContainer/>,
    params: (props) => <DKPAddStatementParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REPAYMENT_MORTGAGE_REG_RECORD]: {
    deal: () => <DealAddStatementContainer/>,
    params: (props) => <RepaymentMortgageParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.PAYOFF_MORTGAGE_RECORD]: {
    deal: () => <DealAddStatementContainer/>,
    params: (props) => <RepaymentMortgageParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REG_ASSIGNMENT_AGREEMENT]: {
    deal: () => <DealAssignmentAgreement/>,
    params: (props) => <AssignmentAgreementParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.MORTGAGE_REGISTRATION]: {
    deal: () => <DealMortgageRegistrationContainer/>,
    params: (props) => <DealMortgageRegistrationParamsContainer onChange={props.onChange}/> 
  },
  [RegistrationTypes.MAKING_CHANGES_STATEMENT]: {
    deal: () => <DealMakingChangesContainer/>,
    params: (props) => <MakingChangesParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.ENTERING_INFORMATION_TO_EGRN]: {
    deal: () => <DealEnteringInfoToEGRN/>,
    params: (props) => <EnteringInfoParamsContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REG_RENT_AGREEMENT]: {
    deal: () => <DealRegRentAgreementContainer/>,
    params: null
  },
  [RegistrationTypes.REG_GKU_W_RIGHT]: {
    deal: () => <DealGKUwRightContainer/>,
    params: null
  },
  [RegistrationTypes.CHANGE_SPECIFICATION_WO_RIGHT]: {
    deal: () => <DealChangeSpecificationContainer/>,
    params: (props) => <MakingChangesSpecificationContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REG_GKU_WO_RIGHT]: {
    deal: () => <DealGKUwoRightContainer/>,
    params: null
  },
  [RegistrationTypes.REG_SHARE_COMMON_PROPERTY]: {
    deal: () => <DealRegShareCommonPropContainer/>,
    params: (props) => <AddRegShareCommonPropContainer onChange={props.onChange}/>
  },
  [RegistrationTypes.REG_RIGHT]: {
    deal: () => <RegRightDealContainer/>,
    params: null
  },
  [RegistrationTypes.PROPERTY_DEREGISTRATION]: {
    deal: () => <PropertyDeregistrationDealContainer/>,
    params: null
  },
  [RegistrationTypes.TERMINATION_OF_OWNERSHIP_RIGHTS]: {
    deal: () => <TerminationOfOwnershipContainer/>,
    params: (props) => <DealTerminationParamsContainer onChange={props.onChange}/>
  }
};